'use client';

import { FC } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip } from '@mui/material';
import { useDialogStore } from '@/stores/dialogStore';

/**
 * Post-create follow-up for bulk shipping: submit now, open the shipping page, or stay on bulk.
 */
export const BulkShippingCreatedDialog: FC = () => {
  const { bulkShippingCreatedDialog, closeBulkShippingCreatedDialog } = useDialogStore();
  const { isOpen, isLocalTransfer, canSubmit, submitDisabledReason } = bulkShippingCreatedDialog;

  const title = isLocalTransfer ? 'Local transfer created' : 'Shipping request created';
  const message = isLocalTransfer
    ? 'The local transfer was saved as pending. Submit it now, open it to review, or close and continue in bulk.'
    : 'The shipping request was saved as pending. Submit it now to send it for processing, open it to review, or close and continue in bulk.';

  const handleChoice = (choice: 'submit' | 'view' | 'close') => {
    closeBulkShippingCreatedDialog(choice);
  };

  return (
    <Dialog open={isOpen} onClose={() => handleChoice('close')} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
        {!canSubmit && submitDisabledReason ? (
          <DialogContentText sx={{ mt: 2, color: 'warning.main' }}>
            Submit is unavailable: {submitDisabledReason}
          </DialogContentText>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => handleChoice('close')}>Close</Button>
        <Button onClick={() => handleChoice('view')}>View shipping</Button>
        <Tooltip title={!canSubmit ? (submitDisabledReason ?? 'Cannot submit yet') : ''}>
          <span>
            <Button variant="contained" onClick={() => handleChoice('submit')} disabled={!canSubmit} autoFocus>
              {isLocalTransfer ? 'Submit transfer' : 'Submit shipping'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};
